import React from 'react'
import { MotionConfig } from 'motion/react'

/**
 * The reduced-motion mechanism for everything CSS cannot reach.
 *
 * entrypoints/application.css collapses the duration tokens and forces
 * `transition-duration: 0.01ms !important` under
 * `prefers-reduced-motion: reduce`, which handles anything animated by a
 * stylesheet. Framer drives transforms through requestAnimationFrame and
 * inline styles instead, so none of that applies to it: six components here
 * animate through `motion/react`, and before this only two of them - HeroSection
 * and LodgeFinder - consulted the preference at all. The sidebar and navbar
 * indicators, the file-upload drop target and the search placeholder animated
 * regardless of what the platform had been asked for.
 *
 * `reducedMotion="user"` makes every descendant motion component read the
 * media query itself and skip transform and layout animations when it is set,
 * which is the same single global switch the stylesheet provides, in the one
 * place the stylesheet cannot go. It replaces per-component `useReducedMotion`
 * checks rather than adding to them - HeroSection and LodgeFinder each carried
 * their own, and each suppressed the whole entry animation including the fade.
 *
 * `reducedMotion="user"` disables transform and layout animations and leaves
 * opacity alone, which is Framer's documented behaviour and the right line:
 * movement is what triggers vestibular symptoms, a cross-fade is not, and
 * removing the fade as well left elements appearing with no transition at all.
 *
 * Every React root wraps in this. motionProvider.test.ts enforces
 * that, because a new entrypoint that forgets it fails silently: the
 * animations simply keep running for the people who asked them not to.
 */
export function MotionProvider({ children }: { children: React.ReactNode }) {
  return <MotionConfig reducedMotion="user">{children}</MotionConfig>
}
